import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

const CustomerNotificationCentre = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const handleNotificationUpdate = (payload) => {
    if (payload?.eventType === 'INSERT') {
      // New notification - convert from snake_case
      const newNotification = {
        id: payload?.new?.id,
        userId: payload?.new?.user_id,
        role: payload?.new?.role,
        type: payload?.new?.type,
        title: payload?.new?.title,
        message: payload?.new?.message,
        actionUrl: payload?.new?.action_url,
        entityId: payload?.new?.entity_id,
        entityType: payload?.new?.entity_type,
        isRead: payload?.new?.is_read,
        readAt: payload?.new?.read_at,
        createdAt: payload?.new?.created_at,
        metadata: payload?.new?.metadata
      };
      setNotifications(prev => [newNotification, ...prev]);
    } else if (payload?.eventType === 'UPDATE') {
      setNotifications(prev =>
        prev?.map(n =>
          n?.id === payload?.new?.id
            ? {
                id: payload?.new?.id,
                userId: payload?.new?.user_id,
                role: payload?.new?.role,
                type: payload?.new?.type,
                title: payload?.new?.title,
                message: payload?.new?.message,
                actionUrl: payload?.new?.action_url,
                entityId: payload?.new?.entity_id,
                entityType: payload?.new?.entity_type,
                isRead: payload?.new?.is_read,
                readAt: payload?.new?.read_at,
                createdAt: payload?.new?.created_at,
                metadata: payload?.new?.metadata
              }
            : n
        )
      );
    } else if (payload?.eventType === 'DELETE') {
      setNotifications(prev => prev?.filter(n => n?.id !== payload?.old?.id));
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadNotifications();

    // Subscribe to real-time updates
    const channel = notificationService?.subscribeToNotifications(
      user?.id,
      'customer',
      handleNotificationUpdate
    );

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user, navigate]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await notificationService?.getAll('customer');
      setNotifications(data);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      setNotifications(prev =>
        prev?.map(n =>
          n?.id === notificationId
            ? { ...n, isRead: true, readAt: new Date()?.toISOString() }
            : n
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await notificationService?.markAllAsRead('customer');
      setNotifications(prev =>
        prev?.map(n => ({
          ...n,
          isRead: true,
          readAt: new Date()?.toISOString()
        }))
      );
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError(err?.message || 'Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification?.isRead) {
      handleMarkAsRead(notification?.id);
    }

    // Navigate if action URL exists
    if (notification?.actionUrl) {
      navigate(notification?.actionUrl);
    }
  };

  const getFilteredNotifications = () => {
    return notifications?.filter(n => {
      const typeMatch = filterType === 'all' || n?.type === filterType;
      const statusMatch =
        filterStatus === 'all' ||
        (filterStatus === 'unread' && !n?.isRead) ||
        (filterStatus === 'read' && n?.isRead);
      return typeMatch && statusMatch;
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'quote':
        return '💰';
      case 'message':
        return '💬';
      case 'enquiry':
        return '✈️';
      case 'alert':
        return '⚠️';
      default:
        return '🔔';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date?.toLocaleDateString();
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-600 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>

          {unreadCount > 0 && (
            <Button
              onClick={handleMarkAllAsRead}
              disabled={markingAllRead}
              variant="outline"
            >
              {markingAllRead ? 'Marking...' : 'Mark All Read'}
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Types</option>
                <option value="quote">Quote Received</option>
                <option value="message">Messages</option>
                <option value="enquiry">Enquiry Updates</option>
                <option value="alert">Alerts</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              <span className="ml-3 text-gray-600">Loading notifications...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Notifications List */}
        {!loading && !error && (
          <div className="space-y-3">
            {filteredNotifications?.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">🔔</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No notifications
                </h3>
                <p className="text-gray-600">
                  {filterType !== 'all' || filterStatus !== 'all' ? 'Try adjusting your filters' : 'You are all caught up!'}
                </p>
              </div>
            ) : (
              filteredNotifications?.map((notification) => (
                <div
                  key={notification?.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    bg-white rounded-lg shadow-sm border cursor-pointer
                    transition-all duration-200 hover:shadow-md
                    ${
                      notification?.isRead
                        ? 'border-gray-200 opacity-75' : 'border-teal-500 border-l-4'
                    }
                  `}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0">
                        {getNotificationIcon(notification?.type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3
                            className={`text-lg font-semibold ${
                              notification?.isRead ? 'text-gray-700' : 'text-gray-900'
                            }`}
                          >
                            {notification?.title}
                          </h3>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatTimeAgo(notification?.createdAt)}
                          </span>
                        </div>

                        <p className="text-gray-600 mt-1">{notification?.message}</p>

                        {notification?.actionUrl && (
                          <button className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-2 inline-flex items-center">
                            View Details
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {!notification?.isRead && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerNotificationCentre;